import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useJobStore } from '../../stores/useJobStore';
import TaskCard from '../../components/job/TaskCard';
import { Settings, ChevronDown, Navigation } from 'lucide-react';
import { Checkbox } from '@h2/ui';
import { DataSheetGrid, Column } from 'react-datasheet-grid';
import 'react-datasheet-grid/dist/style.css';
import { getTaskStatusColorHex, getTaskStatusBadgeClass, getTaskStatusTextClass } from '../../utils/taskStatusUtils';
import ProgressBar from '../../components/ui/ProgressBar';
import WorkerOnlineStatus from '../../components/ui/WorkerOnlineStatus';

// 완전한 dependencies가 있는 Job 샘플 데이터 
const sampleJobsData = [
  {
    id: 'job-001',
    title: 'BTS 다큐멘터리 번역 (한영일)',
    sourceLanguage: 'KO',
    targetLanguages: ['EN', 'JA'],
    tasks: [
      { id: 'task-001-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '김민수', baseUnitPrice: 15000, priceAdjustment: 0, finalUnitPrice: 15000, progress: '완료', alertLevel: null, dependencies: [], order: 0, deadline: '2025-09-24T08:00:00Z' },
      { id: 'task-001-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'group', assignedToName: '전사팀', baseUnitPrice: 8000, priceAdjustment: 1000, finalUnitPrice: 9000, progress: '완료', alertLevel: null, dependencies: ['task-001-1'], order: 1, deadline: '2025-09-24T12:00:00Z' },
      { id: 'task-001-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '이지은', baseUnitPrice: 25000, priceAdjustment: 5000, finalUnitPrice: 30000, progress: '진행중', alertLevel: 'warning', dependencies: ['task-001-2'], order: 2, deadline: '2025-09-25T17:00:00Z', progressPercentage: 60, isWorkerOnline: true },
      { id: 'task-001-4', type: '번역', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '박준호', baseUnitPrice: 25000, priceAdjustment: 0, finalUnitPrice: 25000, progress: '대기', alertLevel: null, dependencies: ['task-001-2'], order: 2, deadline: '2025-09-25T19:00:00Z' },
      { id: 'task-001-5', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'ai', assignedToName: 'AI 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-001-3'], order: 3, deadline: '2025-09-26T10:00:00Z', assignmentStartTime: '2025-09-25T10:30:00Z' },
      { id: 'task-001-6', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-001-4'], order: 3, deadline: '2025-09-27T14:00:00Z', assignmentStartTime: '2025-09-25T08:15:00Z' },
      { id: 'task-001-7', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '대기', alertLevel: null, dependencies: ['task-001-5', 'task-001-6'], order: 4, deadline: '2025-09-30T18:00:00Z', assignmentStartTime: '2025-09-25T14:20:00Z' }
    ]
  },
  {
    id: 'job-002',
    title: 'IVE 뮤직비디오 자막 (한영)',
    sourceLanguage: 'KO',
    targetLanguages: ['EN'],
    tasks: [
      { id: 'task-002-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '정혜진', baseUnitPrice: 15000, priceAdjustment: 2000, finalUnitPrice: 17000, progress: '완료', alertLevel: null, dependencies: [], order: 0, deadline: '2025-09-24T09:00:00Z' },
      { id: 'task-002-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'auto', assignedToName: '작업자 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '완료', alertLevel: null, dependencies: ['task-002-1'], order: 1, deadline: '2025-09-24T13:00:00Z' },
      { id: 'task-002-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '최수연', baseUnitPrice: 25000, priceAdjustment: -2000, finalUnitPrice: 23000, progress: '진행중', alertLevel: null, dependencies: ['task-002-2'], order: 2, deadline: '2025-09-23T10:00:00Z', progressPercentage: 40, isWorkerOnline: false },
      { id: 'task-002-4', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'group', assignedToName: '검수팀', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-002-3'], order: 3, deadline: '2025-09-25T13:00:00Z' },
      { id: 'task-002-5', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '대기', alertLevel: null, dependencies: ['task-002-4'], order: 4, deadline: '2025-09-28T16:00:00Z', assignmentStartTime: '2025-09-25T16:45:00Z' }
    ]
  },
  {
    id: 'job-003',
    title: 'NewJeans 인터뷰 다국어 번역 (한영일중)',
    sourceLanguage: 'KO',
    targetLanguages: ['EN', 'JA', 'ZH'],
    tasks: [
      { id: 'task-003-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '김민수', baseUnitPrice: 15000, priceAdjustment: 0, finalUnitPrice: 15000, progress: '완료', alertLevel: null, dependencies: [], order: 0, deadline: '2025-09-24T11:00:00Z' },
      { id: 'task-003-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'group', assignedToName: '전사팀', baseUnitPrice: 8000, priceAdjustment: 500, finalUnitPrice: 8500, progress: '완료', alertLevel: null, dependencies: ['task-003-1'], order: 1, deadline: '2025-09-24T16:00:00Z' },
      { id: 'task-003-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '이지은', baseUnitPrice: 25000, priceAdjustment: 3000, finalUnitPrice: 28000, progress: '완료', alertLevel: null, dependencies: ['task-003-2'], order: 2, deadline: '2025-09-25T14:30:00Z' },
      { id: 'task-003-4', type: '번역', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '박준호', baseUnitPrice: 25000, priceAdjustment: 0, finalUnitPrice: 25000, progress: '진행중', alertLevel: null, dependencies: ['task-003-2'], order: 2, deadline: '2025-09-25T11:30:00Z', progressPercentage: 80, isWorkerOnline: true },
      { id: 'task-003-5', type: '번역', sourceLanguage: 'KO', targetLanguage: 'ZH', assignmentType: 'auto', assignedToName: '작업자 자동 배정', baseUnitPrice: 25000, priceAdjustment: 0, finalUnitPrice: 25000, progress: '대기', alertLevel: null, dependencies: ['task-003-2'], order: 2, deadline: '2025-09-26T09:00:00Z' },
      { id: 'task-003-6', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'ai', assignedToName: 'AI 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-003-3'], order: 3, deadline: '2025-09-26T15:00:00Z' },
      { id: 'task-003-7', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '최수연', baseUnitPrice: 12000, priceAdjustment: 1000, finalUnitPrice: 13000, progress: '대기', alertLevel: null, dependencies: ['task-003-4'], order: 3, deadline: '2025-09-27T10:00:00Z' },
      { id: 'task-003-8', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'ZH', assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-003-5'], order: 3, deadline: '2025-09-29T12:00:00Z' },
      { id: 'task-003-9', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '대기', alertLevel: null, dependencies: ['task-003-6', 'task-003-7', 'task-003-8'], order: 4, deadline: '2025-09-30T17:00:00Z' }
    ]
  },
  {
    id: 'job-004',
    title: 'BLACKPINK 글로벌 인터뷰 (한영일중프)',
    sourceLanguage: 'KO',
    targetLanguages: ['EN', 'JA', 'ZH', 'FR'],
    tasks: [
      { id: 'task-004-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '김민수', baseUnitPrice: 18000, priceAdjustment: 2000, finalUnitPrice: 20000, progress: '완료', alertLevel: null, dependencies: [], order: 0 },
      { id: 'task-004-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'group', assignedToName: '전사팀', baseUnitPrice: 10000, priceAdjustment: 0, finalUnitPrice: 10000, progress: '완료', alertLevel: null, dependencies: ['task-004-1'], order: 1 },
      { id: 'task-004-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '이지은', baseUnitPrice: 30000, priceAdjustment: 5000, finalUnitPrice: 35000, progress: '완료', alertLevel: null, dependencies: ['task-004-2'], order: 2 },
      { id: 'task-004-4', type: '번역', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '박준호', baseUnitPrice: 28000, priceAdjustment: 2000, finalUnitPrice: 30000, progress: '진행중', alertLevel: null, dependencies: ['task-004-2'], order: 2 },
      { id: 'task-004-5', type: '번역', sourceLanguage: 'KO', targetLanguage: 'ZH', assignmentType: 'worker', assignedToName: '왕리나', baseUnitPrice: 32000, priceAdjustment: 0, finalUnitPrice: 32000, progress: '대기', alertLevel: null, dependencies: ['task-004-2'], order: 2 },
      { id: 'task-004-6', type: '번역', sourceLanguage: 'KO', targetLanguage: 'FR', assignmentType: 'auto', assignedToName: '작업자 자동 배정', baseUnitPrice: 35000, priceAdjustment: 0, finalUnitPrice: 35000, progress: '대기', alertLevel: null, dependencies: ['task-004-2'], order: 2 },
      { id: 'task-004-7', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'ai', assignedToName: 'AI 자동 배정', baseUnitPrice: 15000, priceAdjustment: 0, finalUnitPrice: 15000, progress: '대기', alertLevel: null, dependencies: ['task-004-3'], order: 3 },
      { id: 'task-004-8', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '최수연', baseUnitPrice: 14000, priceAdjustment: 1000, finalUnitPrice: 15000, progress: '대기', alertLevel: null, dependencies: ['task-004-4'], order: 3 },
      { id: 'task-004-9', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'ZH', assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 16000, priceAdjustment: 0, finalUnitPrice: 16000, progress: '대기', alertLevel: null, dependencies: ['task-004-5'], order: 3 },
      { id: 'task-004-10', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'FR', assignmentType: 'group', assignedToName: '검수팀', baseUnitPrice: 18000, priceAdjustment: 0, finalUnitPrice: 18000, progress: '대기', alertLevel: null, dependencies: ['task-004-6'], order: 3 },
      { id: 'task-004-11', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-004-7', 'task-004-8', 'task-004-9', 'task-004-10'], order: 4 }
    ]
  },
  // 추가 샘플 데이터 (기존 데이터 복사하여 이름만 변경)
  {
    id: 'job-005',
    title: 'BTS 다큐멘터리 번역 0',
    sourceLanguage: 'KO',
    targetLanguages: ['EN', 'JA'],
    tasks: [
      { id: 'task-005-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '김민수', baseUnitPrice: 15000, priceAdjustment: 0, finalUnitPrice: 15000, progress: '완료', alertLevel: null, dependencies: [], order: 0 },
      { id: 'task-005-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'group', assignedToName: '전사팀', baseUnitPrice: 8000, priceAdjustment: 1000, finalUnitPrice: 9000, progress: '완료', alertLevel: null, dependencies: ['task-005-1'], order: 1 },
      { id: 'task-005-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '이지은', baseUnitPrice: 25000, priceAdjustment: 5000, finalUnitPrice: 30000, progress: '진행중', alertLevel: 'warning', dependencies: ['task-005-2'], order: 2 },
      { id: 'task-005-4', type: '번역', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '박준호', baseUnitPrice: 25000, priceAdjustment: 0, finalUnitPrice: 25000, progress: '대기', alertLevel: null, dependencies: ['task-005-2'], order: 2 },
      { id: 'task-005-5', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'ai', assignedToName: 'AI 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-005-3'], order: 3 },
      { id: 'task-005-6', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-005-4'], order: 3 },
      { id: 'task-005-7', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '대기', alertLevel: null, dependencies: ['task-005-5', 'task-005-6'], order: 4 }
    ]
  },
  {
    id: 'job-006',
    title: 'IVE 뮤직비디오 자막 1',
    sourceLanguage: 'KO',
    targetLanguages: ['EN'],
    tasks: [
      { id: 'task-006-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '정혜진', baseUnitPrice: 15000, priceAdjustment: 2000, finalUnitPrice: 17000, progress: '완료', alertLevel: null, dependencies: [], order: 0 },
      { id: 'task-006-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'auto', assignedToName: '작업자 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '완료', alertLevel: null, dependencies: ['task-006-1'], order: 1 },
      { id: 'task-006-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '최수연', baseUnitPrice: 25000, priceAdjustment: -2000, finalUnitPrice: 23000, progress: '진행중', alertLevel: null, dependencies: ['task-006-2'], order: 2, progressPercentage: 20, isWorkerOnline: true },
      { id: 'task-006-4', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'group', assignedToName: '검수팀', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-006-3'], order: 3 },
      { id: 'task-006-5', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '대기', alertLevel: null, dependencies: ['task-006-4'], order: 4 }
    ]
  },
  {
    id: 'job-007',
    title: 'NewJeans 인터뷰 다국어 번역 2',
    sourceLanguage: 'KO',
    targetLanguages: ['EN', 'JA', 'ZH'],
    tasks: [
      { id: 'task-007-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '김민수', baseUnitPrice: 15000, priceAdjustment: 0, finalUnitPrice: 15000, progress: '완료', alertLevel: null, dependencies: [], order: 0 },
      { id: 'task-007-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'group', assignedToName: '전사팀', baseUnitPrice: 8000, priceAdjustment: 500, finalUnitPrice: 8500, progress: '완료', alertLevel: null, dependencies: ['task-007-1'], order: 1 },
      { id: 'task-007-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '이지은', baseUnitPrice: 25000, priceAdjustment: 3000, finalUnitPrice: 28000, progress: '완료', alertLevel: null, dependencies: ['task-007-2'], order: 2 },
      { id: 'task-007-4', type: '번역', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '박준호', baseUnitPrice: 25000, priceAdjustment: 0, finalUnitPrice: 25000, progress: '진행중', alertLevel: null, dependencies: ['task-007-2'], order: 2 },
      { id: 'task-007-5', type: '번역', sourceLanguage: 'KO', targetLanguage: 'ZH', assignmentType: 'auto', assignedToName: '작업자 자동 배정', baseUnitPrice: 25000, priceAdjustment: 0, finalUnitPrice: 25000, progress: '대기', alertLevel: null, dependencies: ['task-007-2'], order: 2 },
      { id: 'task-007-6', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'ai', assignedToName: 'AI 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-007-3'], order: 3 },
      { id: 'task-007-7', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '최수연', baseUnitPrice: 12000, priceAdjustment: 1000, finalUnitPrice: 13000, progress: '대기', alertLevel: null, dependencies: ['task-007-4'], order: 3 },
      { id: 'task-007-8', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'ZH', assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-007-5'], order: 3 },
      { id: 'task-007-9', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '대기', alertLevel: null, dependencies: ['task-007-6', 'task-007-7', 'task-007-8'], order: 4 }
    ]
  },
  {
    id: 'job-008',
    title: 'BLACKPINK 글로벌 인터뷰 3',
    sourceLanguage: 'KO',
    targetLanguages: ['EN', 'JA', 'ZH', 'FR'],
    tasks: [
      { id: 'task-008-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '김민수', baseUnitPrice: 15000, priceAdjustment: 0, finalUnitPrice: 15000, progress: '완료', alertLevel: null, dependencies: [], order: 0 },
      { id: 'task-008-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'group', assignedToName: '전사팀', baseUnitPrice: 8000, priceAdjustment: 500, finalUnitPrice: 8500, progress: '완료', alertLevel: null, dependencies: ['task-008-1'], order: 1 },
      { id: 'task-008-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '이지은', baseUnitPrice: 25000, priceAdjustment: 3000, finalUnitPrice: 28000, progress: '완료', alertLevel: null, dependencies: ['task-008-2'], order: 2 },
      { id: 'task-008-4', type: '번역', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '박준호', baseUnitPrice: 25000, priceAdjustment: 0, finalUnitPrice: 25000, progress: '진행중', alertLevel: null, dependencies: ['task-008-2'], order: 2 },
      { id: 'task-008-5', type: '번역', sourceLanguage: 'KO', targetLanguage: 'ZH', assignmentType: 'auto', assignedToName: '작업자 자동 배정', baseUnitPrice: 25000, priceAdjustment: 0, finalUnitPrice: 25000, progress: '대기', alertLevel: null, dependencies: ['task-008-2'], order: 2 },
      { id: 'task-008-6', type: '번역', sourceLanguage: 'KO', targetLanguage: 'FR', assignmentType: 'worker', assignedToName: '최수연', baseUnitPrice: 28000, priceAdjustment: 2000, finalUnitPrice: 30000, progress: '대기', alertLevel: null, dependencies: ['task-008-2'], order: 2 },
      { id: 'task-008-7', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'ai', assignedToName: 'AI 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-008-3'], order: 3 },
      { id: 'task-008-8', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '최수연', baseUnitPrice: 12000, priceAdjustment: 1000, finalUnitPrice: 13000, progress: '대기', alertLevel: null, dependencies: ['task-008-4'], order: 3 },
      { id: 'task-008-9', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'ZH', assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-008-5'], order: 3 },
      { id: 'task-008-10', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'FR', assignmentType: 'worker', assignedToName: '이지은', baseUnitPrice: 13000, priceAdjustment: 0, finalUnitPrice: 13000, progress: '대기', alertLevel: null, dependencies: ['task-008-6'], order: 3 },
      { id: 'task-008-11', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '대기', alertLevel: null, dependencies: ['task-008-7', 'task-008-8', 'task-008-9', 'task-008-10'], order: 4 }
    ]
  },
  {
    id: 'job-009',
    title: 'BTS 다큐멘터리 번역 4',
    sourceLanguage: 'KO',
    targetLanguages: ['EN', 'JA'],
    tasks: [
      { id: 'task-009-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '김민수', baseUnitPrice: 15000, priceAdjustment: 0, finalUnitPrice: 15000, progress: '완료', alertLevel: null, dependencies: [], order: 0 },
      { id: 'task-009-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'group', assignedToName: '전사팀', baseUnitPrice: 8000, priceAdjustment: 1000, finalUnitPrice: 9000, progress: '완료', alertLevel: null, dependencies: ['task-009-1'], order: 1 },
      { id: 'task-009-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '이지은', baseUnitPrice: 25000, priceAdjustment: 5000, finalUnitPrice: 30000, progress: '진행중', alertLevel: 'error', dependencies: ['task-009-2'], order: 2 },
      { id: 'task-009-4', type: '번역', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'worker', assignedToName: '박준호', baseUnitPrice: 25000, priceAdjustment: 0, finalUnitPrice: 25000, progress: '대기', alertLevel: null, dependencies: ['task-009-2'], order: 2 },
      { id: 'task-009-5', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'ai', assignedToName: 'AI 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-009-3'], order: 3 },
      { id: 'task-009-6', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'JA', assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-009-4'], order: 3 },
      { id: 'task-009-7', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '대기', alertLevel: null, dependencies: ['task-009-5', 'task-009-6'], order: 4 }
    ]
  },
  {
    id: 'job-010',
    title: 'IVE 뮤직비디오 자막 5',
    sourceLanguage: 'KO',
    targetLanguages: ['EN'],
    tasks: [
      { id: 'task-010-1', type: '전사', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'worker', assignedToName: '정혜진', baseUnitPrice: 15000, priceAdjustment: 2000, finalUnitPrice: 17000, progress: '완료', alertLevel: null, dependencies: [], order: 0 },
      { id: 'task-010-2', type: '전사검수', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'auto', assignedToName: '작업자 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '완료', alertLevel: null, dependencies: ['task-010-1'], order: 1 },
      { id: 'task-010-3', type: '번역', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'worker', assignedToName: '최수연', baseUnitPrice: 25000, priceAdjustment: -2000, finalUnitPrice: 23000, progress: '진행중', alertLevel: 'warning', dependencies: ['task-010-2'], order: 2 },
      { id: 'task-010-4', type: '번역검수', sourceLanguage: 'KO', targetLanguage: 'EN', assignmentType: 'group', assignedToName: '검수팀', baseUnitPrice: 12000, priceAdjustment: 0, finalUnitPrice: 12000, progress: '대기', alertLevel: null, dependencies: ['task-010-3'], order: 3 },
      { id: 'task-010-5', type: 'PM 확인', sourceLanguage: 'KO', targetLanguage: null, assignmentType: 'pm', assignedToName: 'PM 자동 배정', baseUnitPrice: 8000, priceAdjustment: 0, finalUnitPrice: 8000, progress: '대기', alertLevel: null, dependencies: ['task-010-4'], order: 4 }
    ]
  }
];

type ViewType = 'default' | 'language' | 'tasktype' | 'dag' | 'spreadsheet';

interface FieldVisibility {
  taskType: boolean;
  language: boolean;
  assignee: boolean;
  progress: boolean;
  deadline: boolean;
  price: boolean;
  taskId: boolean;
  jobInfo: boolean;
}

// 스프레드시트 행 타입 정의
interface SpreadsheetRow {
  jobId: string;
  jobTitle: string;
  sourceLanguage: string;
  targetLanguage: string;
  taskType: string;
  assignee: string;
  progress: string;
  deadline: string;
  baseUnitPrice: string;
  priceAdjustment: string;
  finalUnitPrice: string;
  memo: string;
  alertLevel?: 'warning' | 'error' | null;
}

// 정렬 타입 정의
type SortPreset = 'job' | 'taskType' | 'language' | 'jobLanguage';

const WorkManagePage: React.FC = () => {
  // 언어 코드를 한국어로 변환
  const getLanguageName = (code: string) => {
    const languageMap: { [key: string]: string } = {
      'KO': '한국어',
      'EN': '영어', 
      'JA': '일본어',
      'ZH': '중국어',
      'FR': '프랑스어',
      '언어무관': '공통'
    };
    return languageMap[code] || code;
  };

  // URL 쿼리 파라메터 관리
  const [searchParams, setSearchParams] = useSearchParams();
  
  // 실제 스토어 대신 샘플 데이터 사용
  const jobs = sampleJobsData;
  
  // URL에서 초기값 가져오기
  const initialViewType = (searchParams.get('view') as ViewType) || 'default';
  const initialSortPreset = (searchParams.get('sort') as SortPreset) || 'job';
  
  const [viewType, setViewType] = useState<ViewType>(initialViewType);
  const [showViewOptions, setShowViewOptions] = useState(false);
  const [sortPreset, setSortPreset] = useState<SortPreset>(initialSortPreset);

  // 뷰 타입 변경 함수 (URL 파라메터도 업데이트)
  const updateViewType = (newViewType: ViewType) => {
    setViewType(newViewType);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', newViewType);
    setSearchParams(newParams);
  };

  // 정렬 프리셋 변경 함수 (URL 파라메터도 업데이트)
  const updateSortPreset = (newSortPreset: SortPreset) => {
    setSortPreset(newSortPreset);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', newSortPreset);
    setSearchParams(newParams);
  };

  // URL 파라메터 변경 시 상태 동기화
  useEffect(() => {
    const urlViewType = (searchParams.get('view') as ViewType) || 'default';
    const urlSortPreset = (searchParams.get('sort') as SortPreset) || 'job';
    
    if (urlViewType !== viewType) {
      setViewType(urlViewType);
    }
    if (urlSortPreset !== sortPreset) {
      setSortPreset(urlSortPreset);
    }
  }, [searchParams]);

  // 모든 언어 추출 (중복 제거)
  const getAllLanguages = () => {
    const allLanguages = new Set<string>();
    jobs.forEach(job => {
      job.targetLanguages.forEach(lang => allLanguages.add(lang));
      // 언어무관 작업이 있는지 확인
      const hasLanguageIndependent = job.tasks.some(task => task.targetLanguage === null);
      if (hasLanguageIndependent) {
        allLanguages.add('언어무관');
      }
    });
    return Array.from(allLanguages).sort();
  };

  // 언어 필터 상태 (기본적으로 모든 언어 표시)
  const [visibleLanguages, setVisibleLanguages] = useState<Set<string>>(() => {
    return new Set(getAllLanguages());
  });

  // 언어 표시/숨김 토글
  const toggleLanguageVisibility = (language: string) => {
    setVisibleLanguages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(language)) {
        newSet.delete(language);
      } else {
        newSet.add(language);
      }
      return newSet;
    });
  };

  // 미니맵 상태
  const [showMinimap, setShowMinimap] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState({ top: 0, bottom: 0 });
  const [minimapVisible, setMinimapVisible] = useState(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 스크롤 위치 추적 및 자동 숨김
  useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const scrollBottom = scrollTop + viewportHeight;
      
      setScrollPosition({ top: scrollTop, bottom: scrollBottom });
      
      // 미니맵 표시
      setMinimapVisible(true);
      
      // 기존 타이머 제거
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // 3초 후 자동 숨김
      scrollTimeoutRef.current = setTimeout(() => {
        setMinimapVisible(false);
      }, 3000);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll(); // 초기값 설정

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);


  // Job으로 스크롤 이동
  const scrollToJob = (jobId: string) => {
    const jobElement = document.getElementById(`job-${jobId}`);
    if (jobElement) {
      jobElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // 미니맵에서 현재 화면 영역 계산
  const getViewportIndicator = () => {
    if (!showMinimap) return null;

    // 미니맵 컨테이너의 실제 높이 측정
    const minimapContainer = document.querySelector('.minimap-container');
    if (!minimapContainer) return null;
    
    const minimapRect = minimapContainer.getBoundingClientRect();
    const minimapHeight = minimapRect.height;
    
    // 전체 문서의 높이와 현재 스크롤 위치
    const documentHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    const scrollTop = window.scrollY;
    
    // 뷰포트의 상대적 위치 계산 (0~1)
    const maxScrollTop = documentHeight - viewportHeight;
    const relativeTop = maxScrollTop > 0 ? Math.max(0, Math.min(1, scrollTop / maxScrollTop)) : 0;
    const relativeViewportHeight = viewportHeight / documentHeight;
    
    // 미니맵에서의 실제 위치와 크기
    const indicatorHeight = Math.max(8, relativeViewportHeight * minimapHeight);
    const maxIndicatorTop = minimapHeight - indicatorHeight;
    const indicatorTop = Math.min(maxIndicatorTop, relativeTop * maxIndicatorTop);

    return {
      top: indicatorTop,
      height: indicatorHeight
    };
  };

  // Task 필드 표시/숨김 설정
  const [fieldVisibility, setFieldVisibility] = useState<FieldVisibility>({
    taskType: true,
    language: true,
    assignee: true,
    progress: true,
    deadline: true,
    price: false,
    taskId: false,
    jobInfo: true
  });

  // 시간 표기 옵션
  const [timeDisplayOption, setTimeDisplayOption] = useState<'date' | 'datetime' | 'relative'>('date');

  // 상대적 시간 표기 함수
  const getRelativeTime = (deadline: string) => {
    try {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      const diffTime = deadlineDate.getTime() - now.getTime();
      const diffHours = Math.round(diffTime / (1000 * 60 * 60));
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      if (Math.abs(diffHours) < 24) {
        // 24시간 이내는 시간으로 표시
        if (diffHours === 0) return '지금';
        if (diffHours > 0) return `${diffHours}시간 후`;
        return `${Math.abs(diffHours)}시간 전`;
      } else {
        // 24시간 이상은 일수로 표시
        if (diffDays > 0) return `${diffDays}일 후`;
        return `${Math.abs(diffDays)}일 전`;
      }
    } catch {
      return deadline;
    }
  };

  // 시간 표기 포맷 함수
  const formatDeadlineByOption = (deadline: string) => {
    if (deadline === '-') return '-';
    
    try {
      const date = new Date(deadline);
      
      switch (timeDisplayOption) {
        case 'date':
          return date.toLocaleDateString('ko-KR', { 
            month: 'numeric', 
            day: 'numeric' 
          });
        case 'datetime':
          return date.toLocaleDateString('ko-KR', { 
            month: 'numeric', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        case 'relative':
          return getRelativeTime(deadline);
        default:
          return deadline;
      }
    } catch {
      return deadline;
    }
  };

  // 필드 표시/숨김 토글
  const toggleField = (field: keyof FieldVisibility) => {
    setFieldVisibility(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showViewOptions && !target.closest('.view-options-dropdown')) {
        setShowViewOptions(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showViewOptions]);

  // Job 내에서 Task를 언어별로 그룹핑
  const groupTasksByLanguageInJob = (tasks: any[]) => {
    const languageIndependentTasks = tasks.filter(task => !task.targetLanguage);
    const languageDependentTasks = tasks.filter(task => task.targetLanguage);
    
    const groupedByLanguage: { [key: string]: any[] } = {};
    languageDependentTasks.forEach(task => {
      const lang = task.targetLanguage;
      if (!groupedByLanguage[lang]) {
        groupedByLanguage[lang] = [];
      }
      groupedByLanguage[lang].push(task);
    });

    return { languageIndependentTasks, groupedByLanguage };
  };

  const groupTasksByTaskTypeInJob = (tasks: any[]) => {
    const groupedByTaskType: { [key: string]: any[] } = {};
    tasks.forEach(task => {
      const taskType = task.type;
      if (!groupedByTaskType[taskType]) {
        groupedByTaskType[taskType] = [];
      }
      groupedByTaskType[taskType].push(task);
    });

    return groupedByTaskType;
  };

  // 기본 뷰용 언어별 그룹핑 (간단한 행별 배치)
  const groupTasksByLanguageForDefault = (tasks: any[]) => {
    const grouped: { [key: string]: any[] } = {};
    
    tasks.forEach(task => {
      const lang = task.targetLanguage || '공통';
      if (!grouped[lang]) {
        grouped[lang] = [];
      }
      grouped[lang].push(task);
    });

    // 언어별로 정렬 (공통 먼저, 나머지는 알파벳 순)
    const sortedLanguages = Object.keys(grouped).sort((a, b) => {
      if (a === '공통') return -1;
      if (b === '공통') return 1;
      return a.localeCompare(b);
    });

    // 각 언어 내에서 order로 정렬
    sortedLanguages.forEach(lang => {
      grouped[lang].sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    return { grouped, sortedLanguages };
  };

  // 기본 뷰 렌더링
  const renderDefaultView = () => {
    return (
      <div className="space-y-2">
        {jobs.map(job => (
          <div key={job.id} id={`job-${job.id}`} className="bg-white border rounded-lg shadow-sm">
            <div className="flex">
              {/* 좌측: Job 정보 */}
              {fieldVisibility.jobInfo && (
                <div className="w-48 flex-shrink-0 border-r bg-slate-50 p-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{job.title}</h3>
                    <div className="text-sm">
                      <div className="text-slate-600">
                        {getLanguageName(job.sourceLanguage)} → {job.targetLanguages.map(lang => getLanguageName(lang)).join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 우측: Task 카드들 */}
              <div className="flex-1 p-4">
                {(() => {
                  const { grouped, sortedLanguages } = groupTasksByLanguageForDefault(job.tasks);
                  // 표시되는 언어만 필터링
                  const filteredLanguages = sortedLanguages.filter(language => {
                    const langCode = language === '공통' ? '언어무관' : language;
                    return visibleLanguages.has(langCode);
                  });
                  
                  return (
                    <div className="space-y-2">
                      {filteredLanguages.map(language => (
                        <div key={language}>
                          <div className="text-sm font-medium text-slate-700 mb-1">{getLanguageName(language)}</div>
                          <div className="flex flex-wrap gap-1">
                            {grouped[language].map(task => (
                              <TaskCard 
                                key={task.id} 
                                task={task} 
                                showLanguage={false} // 언어는 행 헤더에 표시됨
                                showTaskType={true}
                                compact={true}
                                fieldVisibility={fieldVisibility}
                                formatDeadline={formatDeadlineByOption}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // 언어별 뷰 렌더링
  const renderLanguageView = () => {
    return (
      <div className="space-y-2">
        {jobs.map(job => {
          const { languageIndependentTasks, groupedByLanguage } = groupTasksByLanguageInJob(job.tasks);
          const languages = Object.keys(groupedByLanguage).sort();
          // 표시되는 언어만 필터링
          const filteredLanguages = languages.filter(lang => visibleLanguages.has(lang));
          const showLanguageIndependent = visibleLanguages.has('언어무관');
          
          return (
            <div key={job.id} id={`job-${job.id}`} className="bg-white border rounded-lg shadow-sm">
              <div className="flex">
                {/* 좌측: Job 정보 */}
                {fieldVisibility.jobInfo && (
                  <div className="w-48 flex-shrink-0 border-r bg-slate-50 p-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{job.title}</h3>
                      <div className="text-sm">
                        <div className="text-slate-600">
                          {getLanguageName(job.sourceLanguage)} → {job.targetLanguages.map(lang => getLanguageName(lang)).join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 우측: 언어별 컬럼 */}
                <div className="flex-1 p-4">
                  <div className="flex gap-0.5">
                    {/* 언어무관 작업 컬럼 */}
                    {languageIndependentTasks.length > 0 && showLanguageIndependent && (
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-slate-700 mb-1">공통</div>
                        <div className="flex flex-col space-y-0">
                          {languageIndependentTasks.map(task => (
                            <TaskCard 
                              key={task.id} 
                              task={task} 
                              showLanguage={false}
                              showTaskType={true}
                              compact={true}
                              fieldVisibility={fieldVisibility}
                              formatDeadline={formatDeadlineByOption}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 각 언어별 컬럼 */}
                    {filteredLanguages.map(language => (
                      <div key={language} className="flex flex-col">
                        <div className="text-sm font-medium text-slate-700 mb-1">{getLanguageName(language)}</div>
                        <div className="flex flex-col space-y-0">
                          {groupedByLanguage[language].map(task => (
                            <TaskCard 
                              key={task.id} 
                              task={task} 
                              showLanguage={false}
                              showTaskType={true}
                              compact={true}
                              fieldVisibility={fieldVisibility}
                              formatDeadline={formatDeadlineByOption}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Task Type별 뷰 렌더링
  const renderTaskTypeView = () => {
    return (
      <div className="space-y-2">
        {jobs.map(job => {
          // 언어 필터에 맞는 task만 표시
          const filteredTasks = job.tasks.filter(task => {
            if (task.targetLanguage === null) {
              return visibleLanguages.has('언어무관');
            }
            return visibleLanguages.has(task.targetLanguage);
          });
          
          const groupedByTaskType = groupTasksByTaskTypeInJob(filteredTasks);
          const taskTypes = Object.keys(groupedByTaskType).sort();
          
          return (
            <div key={job.id} id={`job-${job.id}`} className="bg-white border rounded-lg shadow-sm">
              <div className="flex">
                {/* 좌측: Job 정보 */}
                {fieldVisibility.jobInfo && (
                  <div className="w-48 flex-shrink-0 border-r bg-slate-50 p-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{job.title}</h3>
                      <div className="text-sm">
                        <div className="text-slate-600">
                          {getLanguageName(job.sourceLanguage)} → {job.targetLanguages.map(lang => getLanguageName(lang)).join(', ')}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* 우측: Task Type별 컬럼 */}
                <div className="flex-1 p-4">
                  <div className="flex gap-0.5">
                    {taskTypes.map(taskType => (
                      <div key={taskType} className="flex flex-col">
                        <div className="text-sm font-medium text-slate-700 mb-1">{taskType}</div>
                        <div className="flex flex-col space-y-0">
                          {groupedByTaskType[taskType].map(task => (
                            <TaskCard 
                              key={task.id} 
                              task={task} 
                              showLanguage={true}
                              showTaskType={false}
                              compact={true}
                              fieldVisibility={fieldVisibility}
                              formatDeadline={formatDeadlineByOption}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // DAG 구조를 위한 Task 배치 함수 (네트워크 그래프 형태)
  // 스프레드시트 뷰 렌더링
  const renderSpreadsheetView = () => {
    // 화면 높이에서 헤더 부분을 제외한 동적 높이 계산
    const headerHeight = 200; // 대략적인 헤더 높이
    const dynamicHeight = Math.max(400, window.innerHeight - headerHeight);
    
    return (
      <div className="h-full flex flex-col">
        {/* 스프레드시트 그리드 - 남는 공간 모두 사용 */}
        <div className="border rounded-lg overflow-hidden flex-1">
          <DataSheetGrid
            key={JSON.stringify(fieldVisibility)} // fieldVisibility 변경시 강제 리렌더링
            value={getSpreadsheetData}
            onChange={() => {}} // 읽기 전용
            columns={spreadsheetColumns}
            height={dynamicHeight}
            rowHeight={32}
            addRowsComponent={false} // Add rows 버튼 제거
            createRow={() => ({
              jobId: '',
              jobTitle: '',
              sourceLanguage: '',
              targetLanguage: '',
              taskType: '',
              assignee: '',
              progress: '',
      deadline: '',
              baseUnitPrice: '',
              priceAdjustment: '',
              finalUnitPrice: '',
              memo: ''
            })}
            duplicateRow={({ rowData }) => ({ ...rowData })}
            isRowEmpty={() => false}
            className="react-datasheet-grid"
          />
        </div>
      </div>
    );
  };

  const renderDAGView = () => {
    // 노드 위치 계산 알고리즘
    const calculateNodePositions = (tasks: any[]) => {
      const sortedTasks = tasks.sort((a, b) => (a.order || 0) - (b.order || 0));
      
      // 레이어별로 그룹핑 (order 기준)
      const layers: { [key: number]: any[] } = {};
      sortedTasks.forEach(task => {
        const order = task.order || 0;
        if (!layers[order]) layers[order] = [];
        layers[order].push(task);
      });
      
      const layerKeys = Object.keys(layers).map(Number).sort((a, b) => a - b);
      const NODE_WIDTH = 144; // TaskCard w-36 = 144px
      const NODE_HEIGHT = 120; // TaskCard 실제 높이에 맞춤
      const LAYER_SPACING = 200; // 가로 여백 축소 (더 컴팩트)
      const NODE_SPACING = 180; // 세로 여백 대폭 증가 (TaskCard 높이 + 충분한 여백)
      
      // 각 노드의 위치 계산
      const nodePositions: { [key: string]: { x: number, y: number, task: any } } = {};
      
      layerKeys.forEach((layerIndex, i) => {
        const layerTasks = layers[layerIndex];
        const layerHeight = (layerTasks.length - 1) * NODE_SPACING;
        const startY = -layerHeight / 2;
        
        layerTasks.forEach((task, j) => {
          nodePositions[task.id] = {
            x: i * LAYER_SPACING,
            y: startY + j * NODE_SPACING,
            task
          };
        });
      });
      
      // 실제 노드들의 Y 위치 범위 계산
      const yPositions = Object.values(nodePositions).map(n => n.y);
      const minY = Math.min(...yPositions);
      const maxY = Math.max(...yPositions);
      
      return { 
        nodePositions, 
        width: (layerKeys.length - 1) * LAYER_SPACING + NODE_WIDTH, 
        height: Math.max(maxY - minY + NODE_HEIGHT + 60, NODE_HEIGHT + 40), // 여백 증가로 잘림 방지
        minY: minY
      };
    };

    // 엣지(연결선) 생성
    const createEdges = (tasks: any[], nodePositions: any) => {
      const edges: any[] = [];
      
      tasks.forEach(task => {
        if (task.dependencies && task.dependencies.length > 0) {
          task.dependencies.forEach((depId: string) => {
            const fromNode = nodePositions[depId];
            const toNode = nodePositions[task.id];
            
            if (fromNode && toNode) {
              edges.push({
                from: fromNode,
                to: toNode,
                id: `${depId}-${task.id}`
              });
            }
          });
        }
      });
      
      return edges;
  };

  return (
      <div className="space-y-2">
        {jobs.map(job => {
          // 언어 필터에 맞는 task만 표시
          const filteredTasks = job.tasks.filter(task => {
            if (task.targetLanguage === null) {
              return visibleLanguages.has('언어무관');
            }
            return visibleLanguages.has(task.targetLanguage);
          });
          
          const { nodePositions, width, height, minY } = calculateNodePositions(filteredTasks);
          const edges = createEdges(filteredTasks, nodePositions);
          const SVG_PADDING = 20; // 위아래 여백 적당히
          const NODE_WIDTH = 144; // TaskCard w-36 = 144px
          const NODE_HEIGHT = 120; // TaskCard 실제 높이와 일치
          
            return (
              <div key={job.id} id={`job-${job.id}`} className="bg-white border rounded-lg shadow-sm">
                <div className="flex">
                {/* 좌측: Job 정보 */}
                {fieldVisibility.jobInfo && (
                  <div className="w-48 flex-shrink-0 border-r bg-slate-50 p-4">
        <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{job.title}</h3>
                      <div className="text-sm">
                        <div className="text-slate-600">
                          {getLanguageName(job.sourceLanguage)} → {job.targetLanguages.map(lang => getLanguageName(lang)).join(', ')}
        </div>
                      </div>
                    </div>
                  </div>
                )}

                  {/* 우측: DAG 네트워크 그래프 */}
                  <div className="flex-1 p-4">
                    <div className="relative w-full">
                      <div className="relative" style={{ width: width + SVG_PADDING * 2, height: height + SVG_PADDING * 2 }}>
                    {/* SVG for edges */}
                    <svg 
                      className="absolute inset-0 pointer-events-none"
                      width={width + SVG_PADDING * 2}
                      height={height + SVG_PADDING * 2}
                      style={{ zIndex: 1 }}
                    >
                      {edges.map(edge => {
                        // 노드의 중심점 계산 (수직 중앙)
                        const fromCenterX = edge.from.x + SVG_PADDING + NODE_WIDTH / 2;
                        const fromCenterY = edge.from.y - minY + SVG_PADDING + NODE_HEIGHT / 2;
                        const toCenterX = edge.to.x + SVG_PADDING + NODE_WIDTH / 2;
                        const toCenterY = edge.to.y - minY + SVG_PADDING + NODE_HEIGHT / 2;
                        
                        // 선의 실제 시작/끝점: 노드 테두리에서 시작/끝하되 높이는 중앙
                        const fromEdgeX = edge.from.x + SVG_PADDING + NODE_WIDTH; // 시작 노드의 오른쪽 테두리
                        const toEdgeX = edge.to.x + SVG_PADDING;                  // 끝 노드의 왼쪽 테두리
                        
                        // 자연스러운 곡률 적용
                        const deltaX = toEdgeX - fromEdgeX;
                        const deltaY = toCenterY - fromCenterY;
                        
                        let pathData;
                        
                        if (Math.abs(deltaY) < 10) {
                          // 같은 높이: 직선
                          pathData = `M ${fromEdgeX} ${fromCenterY} L ${toEdgeX} ${toCenterY}`;
                        } else {
                          // 높이 차이가 있을 때: 고정된 곡률로 자연스럽게
                          const midX = fromEdgeX + deltaX * 0.6; // 60% 지점에서 곡선 시작
                          const curveRadius = 20; // 고정된 곡률 반지름
                          
                          // 수평으로 직진 후 곡선으로 연결
                          pathData = `M ${fromEdgeX} ${fromCenterY} 
                                     L ${midX - curveRadius} ${fromCenterY}
                                     Q ${midX} ${fromCenterY} ${midX} ${fromCenterY + (deltaY > 0 ? curveRadius : -curveRadius)}
                                     L ${midX} ${toCenterY - (deltaY > 0 ? curveRadius : -curveRadius)}
                                     Q ${midX} ${toCenterY} ${midX + curveRadius} ${toCenterY}
                                     L ${toEdgeX} ${toCenterY}`;
                        }
                        
                        return (
                          <path
                            key={edge.id}
                            d={pathData}
                            stroke="#9CA3AF"
                            strokeWidth="2"
                            fill="none"
                          />
                        );
                      })}
                    </svg>
                    
                    {/* Nodes */}
                    {Object.values(nodePositions).map(({ x, y, task }) => (
                      <div
                        key={task.id}
                        className="absolute"
                        style={{
                          left: x + SVG_PADDING,
                          top: y - minY + SVG_PADDING, // minY 기준으로 조정하여 위쪽 공간 제거
                          zIndex: 2
                        }}
                      >
                        <TaskCard 
                          task={task} 
                          showLanguage={true} // 모든 노드에 언어 정보 표시
                          showTaskType={true}
                          compact={true}
                          fieldVisibility={fieldVisibility}
                          formatDeadline={formatDeadlineByOption}
                        />
                      </div>
                    ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 미니맵 렌더링
  // 스프레드시트용 데이터 변환 및 정렬
  const getSpreadsheetData = useMemo(() => {
    // 모든 task를 행으로 변환
    const allTasks: SpreadsheetRow[] = [];
    
    jobs.forEach(job => {
      job.tasks.forEach(task => {
        const sourceLanguageDisplay = getLanguageName(task.sourceLanguage);
        const targetLanguageDisplay = task.targetLanguage ? getLanguageName(task.targetLanguage) : '-';
        const basePrice = task.baseUnitPrice ? `₩${task.baseUnitPrice.toLocaleString()}` : '-';
        const adjustment = task.priceAdjustment !== undefined && task.priceAdjustment !== 0 
          ? `${task.priceAdjustment >= 0 ? '+' : ''}${task.priceAdjustment.toLocaleString()}`
          : '-';
        const finalPrice = task.finalUnitPrice ? `₩${task.finalUnitPrice.toLocaleString()}` : '-';
          
        allTasks.push({
          jobId: job.id,
          jobTitle: job.title,
          sourceLanguage: sourceLanguageDisplay,
          targetLanguage: targetLanguageDisplay,
          taskType: task.type,
          assignee: task.assignedToName || '미할당',
          progress: task.progress,
          deadline: task.deadline || '-',
          baseUnitPrice: basePrice,
          priceAdjustment: adjustment,
          finalUnitPrice: finalPrice,
          memo: task.memo || '',
          alertLevel: task.alertLevel
        });
      });
    });

    // 정렬 적용
    const sortedTasks = [...allTasks].sort((a, b) => {
      switch (sortPreset) {
        case 'job':
          return a.jobId.localeCompare(b.jobId);
        case 'taskType':
          return a.taskType.localeCompare(b.taskType);
        case 'language':
          return a.targetLanguage.localeCompare(b.targetLanguage);
        case 'jobLanguage':
          const jobCompare = a.jobId.localeCompare(b.jobId);
          return jobCompare !== 0 ? jobCompare : a.targetLanguage.localeCompare(b.targetLanguage);
        default:
          return 0;
      }
    });

    return sortedTasks;
  }, [jobs, sortPreset, fieldVisibility]);

  // 스프레드시트 컬럼 정의 (fieldVisibility에 따라 동적으로 생성)
  const spreadsheetColumns: Column<SpreadsheetRow>[] = useMemo(() => [
    // 기본 컬럼들
    {
      component: ({ rowData }) => (
        <div className="p-2 h-8 flex items-center border-l border-gray-200 bg-gray-50 font-medium text-sm">
          <span className="truncate" title={rowData.jobTitle}>
            {rowData.jobTitle}
          </span>
        </div>
      ),
      title: 'Job',
      basis: 250,
      grow: 0
    },
    {
      component: ({ rowData }) => (
        <div className="p-2 h-8 flex items-center border-l border-gray-200 text-sm">
          {rowData.sourceLanguage}
        </div>
      ),
      title: '원어',
      basis: 80,
      grow: 0
    },
    {
      component: ({ rowData }) => (
        <div className="p-2 h-8 flex items-center border-l border-gray-200 bg-gray-25 text-sm">
          {rowData.targetLanguage}
        </div>
      ),
      title: '번역어',
      basis: 80,
      grow: 0
    },
    ...(fieldVisibility.taskType ? [{
      component: ({ rowData }) => (
        <div className="p-2 h-8 flex items-center border-l border-gray-200 text-sm">
          {rowData.taskType}
        </div>
      ),
      title: '유형',
      basis: 100,
      grow: 0
    }] : []),
    {
      component: ({ rowData }) => {
        
        return (
          <div className="p-2 h-8 flex items-center border-l border-gray-200">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTaskStatusBadgeClass(rowData.progress, rowData.alertLevel, rowData.deadline)}`}>
              {rowData.progress}
            </span>
          </div>
        );
      },
      title: '상태',
      basis: 100,
      grow: 0
    },
    ...(fieldVisibility.assignee ? [{
      component: ({ rowData }) => (
        <div className="p-2 h-8 flex items-center border-l border-gray-200 text-sm">
          {rowData.assignee}
        </div>
      ),
      title: '담당자',
      basis: 120,
      grow: 0
    }] : []),
    ...(fieldVisibility.deadline ? [{
      component: ({ rowData }) => {
        const formatDeadline = (deadline: string) => {
          return formatDeadlineByOption(deadline);
        };

        const deadlineText = formatDeadline(rowData.deadline);

        return (
          <div className="p-2 h-8 flex items-center border-l border-gray-200 text-sm">
            <span 
              className={getTaskStatusTextClass(rowData.progress, rowData.alertLevel, rowData.deadline)}
              title={rowData.deadline !== '-' ? rowData.deadline : undefined}
            >
              {deadlineText}
            </span>
          </div>
        );
      },
      title: '마감일',
      basis: 80,
      grow: 0
    }] : []),
    // 단가 관련 컬럼들 (price 옵션에 따라 모두 함께 표시/숨김)
    ...(fieldVisibility.price ? [
      {
        component: ({ rowData }) => (
          <div className="p-2 h-8 flex items-center border-l border-gray-200 text-sm text-right font-medium">
            {rowData.finalUnitPrice}
          </div>
        ),
        title: '최종단가',
        basis: 100,
        grow: 0
      },
      {
        component: ({ rowData }) => (
          <div className="p-2 h-8 flex items-center border-l border-gray-200 text-sm text-right">
            {rowData.baseUnitPrice}
          </div>
        ),
        title: '단가',
        basis: 100,
        grow: 0
      },
      {
        component: ({ rowData }) => (
          <div className="p-2 h-8 flex items-center border-l border-gray-200 text-sm text-center">
            {rowData.priceAdjustment}
          </div>
        ),
        title: '보정금',
        basis: 80,
        grow: 0
      }
    ] : []),
    // 메모 컬럼 (항상 마지막)
    {
      component: ({ rowData }) => (
        <div className="p-2 h-8 flex items-center border-l border-gray-200 text-sm">
          <span className="truncate" title={rowData.memo}>
            {rowData.memo || '-'}
          </span>
        </div>
      ),
      title: '메모',
      basis: 150,
      grow: 1
    }
  ], [fieldVisibility]);

  const renderMinimap = () => {
    if (!showMinimap || viewType === 'spreadsheet') return null;

    const viewportIndicator = getViewportIndicator();

    return (
      <div 
        className={`fixed bottom-2 right-2 bg-gray-300 bg-opacity-30 backdrop-blur-md border border-white/10 p-1.5 z-50 
          transition-all duration-300 ease-out hover:scale-105 transform-gpu
          ${minimapVisible ? 'opacity-100' : 'opacity-25'}`}
        onMouseEnter={() => setMinimapVisible(true)}
      >
        <div className="relative minimap-container">
          <div className="space-y-0.5">
            {jobs.map(job => {
              // 언어 필터 적용된 task들
              const filteredTasks = job.tasks.filter(task => {
                if (task.targetLanguage === null) {
                  return visibleLanguages.has('언어무관');
                }
                return visibleLanguages.has(task.targetLanguage);
              });

              if (filteredTasks.length === 0) return null;

              return (
                <div
                  key={job.id}
                  className="cursor-pointer hover:bg-white hover:bg-opacity-20"
                  onClick={() => scrollToJob(job.id)}
                >
                  <div className="flex">
                    {filteredTasks.map(task => (
                       <div
                         key={task.id}
                         className={`w-1 h-3 ${
                           task.alertLevel === 'error' || task.alertLevel === 'warning' ? 'animate-pulse' : ''
                         }`}
                        style={{ 
                          backgroundColor: getTaskStatusColorHex(task.progress, task.alertLevel, task.deadline),
                          opacity: 0.8
                        }}
                        title={`${task.type} - ${task.progress}`}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* 현재 화면 영역 인디케이터 */}
          {viewportIndicator && (
            <div
              className="absolute left-0 right-0 border border-red-500 pointer-events-none"
              style={{
                top: `${viewportIndicator.top}px`,
                height: `${viewportIndicator.height}px`
              }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">작업 관리</h1>
        
        {/* 언어 필터 체크박스와 정렬 설정 */}
        <div className="flex items-center gap-8">
          {/* 언어 필터 */}
          <div className="flex items-center gap-4">
            <div className="text-sm font-medium text-slate-700">타겟언어:</div>
            <div className="flex items-center gap-3">
              {getAllLanguages().map(language => (
                <label key={language} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={visibleLanguages.has(language)}
                    onCheckedChange={() => toggleLanguageVisibility(language)}
                  />
                  <span className="text-sm text-slate-600">{getLanguageName(language)}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 스프레드시트 정렬 설정 */}
          {viewType === 'spreadsheet' && (
            <div className="flex items-center gap-4">
              <label className="text-sm font-medium text-slate-700">정렬:</label>
              <select 
                value={sortPreset} 
                onChange={(e) => updateSortPreset(e.target.value as SortPreset)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              >
                <option value="job">Job별</option>
                <option value="taskType">Task 유형별</option>
                <option value="language">언어별</option>
                <option value="jobLanguage">Job별 + 언어별</option>
              </select>
            </div>
          )}
        </div>
        
        {/* 뷰 옵션 드롭다운 */}
        <div className="relative">
          <div className="view-options-dropdown">
          <button
            onClick={() => setShowViewOptions(!showViewOptions)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Settings size={16} />
              <span>표시 옵션</span>
              <ChevronDown size={16} className={`transition-transform ${showViewOptions ? 'rotate-180' : ''}`} />
          </button>
          
      {showViewOptions && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                <div className="p-4">
                  {/* 표시 방식 선택 */}
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">표시 방식</h3>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="viewType"
                          value="default"
                          checked={viewType === 'default'}
                          onChange={(e) => updateViewType(e.target.value as ViewType)}
                          className="mr-2"
                        />
                        <span className="text-sm">📋 기본 보기</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="viewType"
                          value="language"
                          checked={viewType === 'language'}
                          onChange={(e) => updateViewType(e.target.value as ViewType)}
                          className="mr-2"
                        />
                        <span className="text-sm">🌐 언어별 보기</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="viewType"
                          value="tasktype"
                          checked={viewType === 'tasktype'}
                          onChange={(e) => updateViewType(e.target.value as ViewType)}
                          className="mr-2"
                        />
                        <span className="text-sm">📋 Task 유형별 보기</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="viewType"
                          value="dag"
                          checked={viewType === 'dag'}
                          onChange={(e) => updateViewType(e.target.value as ViewType)}
                          className="mr-2"
                        />
                        <span className="text-sm">🔀 워크플로 보기</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="viewType"
                          value="spreadsheet"
                          checked={viewType === 'spreadsheet'}
                          onChange={(e) => updateViewType(e.target.value as ViewType)}
                          className="mr-2"
                        />
                        <span className="text-sm">📊 스프레드시트 보기</span>
                      </label>
        </div>
      </div>

                  {/* Task 필드 표시/숨김 */}
                  <div className="border-t pt-4 mb-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Task 필드</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fieldVisibility.taskType}
                          onChange={() => toggleField('taskType')}
                          className="mr-2"
                        />
                        <span className="text-sm">Task 유형</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fieldVisibility.language}
                          onChange={() => toggleField('language')}
                          className="mr-2"
                        />
                        <span className="text-sm">언어</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fieldVisibility.assignee}
                          onChange={() => toggleField('assignee')}
                          className="mr-2"
                        />
                        <span className="text-sm">담당자</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fieldVisibility.progress}
                          onChange={() => toggleField('progress')}
                          className="mr-2"
                        />
                        <span className="text-sm">상태</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fieldVisibility.deadline}
                          onChange={() => toggleField('deadline')}
                          className="mr-2"
                        />
                        <span className="text-sm">마감일</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fieldVisibility.price}
                          onChange={() => toggleField('price')}
                          className="mr-2"
                        />
                        <span className="text-sm">단가</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={fieldVisibility.taskId}
                          onChange={() => toggleField('taskId')}
                          className="mr-2"
                        />
                        <span className="text-sm">Task ID</span>
                      </label>
                    </div>
                    
                    {/* Job 정보 표시 옵션 */}
                    <div className="border-t pt-4">
                      <h3 className="text-sm font-medium text-gray-900 mb-3">레이아웃</h3>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={fieldVisibility.jobInfo}
                            onChange={() => toggleField('jobInfo')}
                            className="mr-2"
                          />
                          <span className="text-sm">좌측 Job 정보</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={showMinimap}
                            onChange={() => setShowMinimap(!showMinimap)}
                            disabled={viewType === 'spreadsheet'}
                            className={`mr-2 ${viewType === 'spreadsheet' ? 'cursor-not-allowed opacity-50' : ''}`}
                          />
                          <span className={`text-sm ${viewType === 'spreadsheet' ? 'text-gray-400' : ''}`}>미니맵</span>
                        </label>
                        
                        <div className="border-t pt-3 mt-3">
                          <div className="text-sm font-medium text-gray-700 mb-2">시간 표기</div>
                          <div className="space-y-2">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="timeDisplay"
                                value="date"
                                checked={timeDisplayOption === 'date'}
                                onChange={(e) => setTimeDisplayOption('date')}
                                className="mr-2"
                              />
                              <span className="text-sm">날짜 (9/24)</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="timeDisplay"
                                value="datetime"
                                checked={timeDisplayOption === 'datetime'}
                                onChange={(e) => setTimeDisplayOption('datetime')}
                                className="mr-2"
                              />
                              <span className="text-sm">날짜+시간 (9/24 14:30)</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name="timeDisplay"
                                value="relative"
                                checked={timeDisplayOption === 'relative'}
                                onChange={(e) => setTimeDisplayOption('relative')}
                                className="mr-2"
                              />
                              <span className="text-sm">상대시간 (2시간 후)</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 뷰 타입별 렌더링 */}
      {viewType === 'default' && renderDefaultView()}
      {viewType === 'language' && renderLanguageView()}
      {viewType === 'tasktype' && renderTaskTypeView()}
      {viewType === 'dag' && renderDAGView()}
      {viewType === 'spreadsheet' && renderSpreadsheetView()}

      {/* 미니맵 */}
      {renderMinimap()}
    </div>
  );
};

export default WorkManagePage;